// src/components/prompt-detail/PromptResponseDisplay.tsx
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Terminal,
  Bot,
  Loader2,
  AlertTriangle,
  CheckCircle,
  ClipboardCopy,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { LLMResponse, calculateCost, getModelById } from "@/lib/types/models";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeHighlight from "rehype-highlight";
import "highlight.js/styles/github-dark.css";
import type { Components } from "react-markdown";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import rehypeSanitize from "rehype-sanitize";

interface PromptResponseDisplayProps {
  isExecuting: boolean | undefined | null;
  executionResult: LLMResponse | null;
  executionError: string | null;
  hideCost?: boolean; // Hide cost information for onboarding
}

const PromptResponseDisplay: React.FC<PromptResponseDisplayProps> = ({
  isExecuting,
  executionResult,
  executionError,
  hideCost = false,
}) => {
  const { toast } = useToast();
  const displayContent = executionResult?.content || "";

  // Calculate cost for top-right display
  let topCost: string | null = null;
  if (
    !hideCost &&
    executionResult &&
    executionResult.usage &&
    executionResult.modelId
  ) {
    const cost = calculateCost(executionResult.usage, executionResult.modelId);
    if (typeof cost === "number") {
      topCost = `$${cost.toFixed(5)}`;
    }
  }

  const renderUnifiedTokenUsage = () => {
    if (!executionResult || !executionResult.usage || !executionResult.modelId)
      return null;
    const { inputTokens, outputTokens } = executionResult.usage;
    const model = getModelById(executionResult.modelId);
    const totalTokens = inputTokens + outputTokens;

    if (hideCost) {
      // Show only token information, no cost
      return (
        <div className="text-xs text-muted-foreground mt-2">
          Tokens: {totalTokens} (Input: {inputTokens}, Output: {outputTokens})
        </div>
      );
    }

    if (!model) {
      console.warn(
        `Model definition not found for ID: ${executionResult.modelId}`,
      );
      return (
        <div className="text-xs text-muted-foreground mt-2">
          Tokens: Input: {inputTokens}, Output: {outputTokens} (Cost calculation
          unavailable: Unknown model)
        </div>
      );
    }
    const cost = calculateCost(executionResult.usage, executionResult.modelId);
    return (
      <div className="text-xs text-muted-foreground mt-2">
        Tokens: {totalTokens} (Input: {inputTokens}, Output: {outputTokens}) |
        Approx. Cost: {cost !== undefined ? `$${cost.toFixed(5)}` : "N/A"}
      </div>
    );
  };

  const components: Components = {
    h1: ({ node, ...props }) => (
      <h1 className="text-xl font-bold mt-4 mb-2" {...props} />
    ),
    h2: ({ node, ...props }) => (
      <h2 className="text-lg font-bold mt-3 mb-2" {...props} />
    ),
    h3: ({ node, ...props }) => (
      <h3 className="text-md font-bold mt-2 mb-1" {...props} />
    ),
    h4: ({ node, ...props }) => (
      <h4 className="text-base font-semibold mt-2 mb-1" {...props} />
    ),
    p: ({ node, children, ...props }) => {
      const hasBlockElementInAst = node?.children?.some(
        (child: any) =>
          child.type === "code" ||
          child.type === "div" ||
          child.tagName === "div" ||
          (child.type === "element" &&
            child.tagName === "span" &&
            child.children?.some(
              (c: any) => c.type === "element" || c.tagName === "div",
            )),
      );

      const containsRenderedBlockChildren = React.Children.toArray(
        children,
      ).some(
        (child) =>
          React.isValidElement(child) &&
          ["div", "pre", "table", "ul", "ol"].includes(
            (child as React.ReactElement).type as string,
          ),
      );

      if (hasBlockElementInAst || containsRenderedBlockChildren) {
        return (
          <div className="mb-1" {...props}>
            {children}
          </div>
        );
      }

      return (
        <p className="mb-1 leading-snug" {...props}>
          {children}
        </p>
      );
    },
    a: ({ node, ...props }) => (
      <a
        className="text-blue-500 hover:underline"
        target="_blank"
        rel="noopener noreferrer"
        {...props}
      />
    ),
    ul: ({ node, ...props }) => (
      <ul className="list-disc pl-6 mb-2" {...props} />
    ),
    ol: ({ node, ...props }) => (
      <ol className="list-decimal pl-6 mb-2" {...props} />
    ),
    li: ({ node, ...props }) => <li className="leading-snug" {...props} />,
    hr: ({ node, ...props }) => (
      <hr
        className="my-3 border-t border-gray-300 dark:border-gray-600"
        {...props}
      />
    ),
    blockquote: ({ node, ...props }) => (
      <blockquote
        className="border-l-4 border-blue-500 pl-4 italic my-2 bg-gray-50 dark:bg-gray-800 py-1 rounded-r"
        {...props}
      />
    ),
    table: ({ node, ...props }) => (
      <div className="overflow-x-auto my-2 border border-gray-200 dark:border-gray-700 rounded-lg">
        <table
          className="min-w-full divide-y divide-gray-300 dark:divide-gray-600"
          {...props}
        />
      </div>
    ),
    thead: ({ node, ...props }) => (
      <thead className="bg-gray-50 dark:bg-gray-800" {...props} />
    ),
    th: ({ node, ...props }) => (
      <th
        className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-700 dark:text-gray-300"
        {...props}
      />
    ),
    td: ({ node, ...props }) => (
      <td className="px-3 py-2 text-sm break-words max-w-xs" {...props} />
    ),
    code: (props) => {
      const { inline, className, children, ...rest } = props as {
        inline?: boolean;
        className?: string;
        children: React.ReactNode;
      };
      return !inline ? (
        <div className="my-2 bg-gray-100 dark:bg-gray-800 p-2 rounded-md overflow-x-auto">
          <code className="text-sm font-mono whitespace-pre-wrap" {...rest}>
            {children}
          </code>
        </div>
      ) : (
        <code
          className="bg-gray-200 dark:bg-gray-700 px-1 py-0.5 rounded text-sm font-mono"
          {...rest}
        >
          {children}
        </code>
      );
    },
    span: ({
      node,
      children,
      ...props
    }: {
      node?: any;
      children?: React.ReactNode;
      [key: string]: any;
    }) => {
      const hasBlockChildren = React.Children.toArray(children).some(
        (child) =>
          React.isValidElement(child) &&
          ["div", "p", "pre"].includes(
            (child as React.ReactElement).type as string,
          ),
      );

      if (hasBlockChildren) {
        return <div {...props}>{children}</div>;
      }

      return <span {...props}>{children}</span>;
    },
  };

  const handleCopyToClipboard = () => {
    if (executionResult && executionResult.content) {
      navigator.clipboard
        .writeText(executionResult.content)
        .then(() => {
          toast({
            title: "Copied to Clipboard",
            description: "The AI insight has been copied.",
          });
        })
        .catch((err) => {
          console.error("Failed to copy text: ", err);
          toast({
            title: "Copy Failed",
            description: "Could not copy the text to clipboard.",
            variant: "destructive",
          });
        });
    }
  };

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 pt-3 bg-indigo-600 text-indigo-50 rounded-t-lg">
        <div className="flex flex-col">
          <CardTitle className="text-xl font-semibold">
            80/20 AI Insight
          </CardTitle>
          {executionResult && executionResult.modelId && (
            <p className="text-xs text-indigo-200 mt-1">
              Model: {executionResult.modelId}
            </p>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {topCost && (
            <div className="text-indigo-100 text-2xl font-semibold">
              Cost: {topCost}
            </div>
          )}
          {executionResult && executionResult.content && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyToClipboard}
              title="Copy Response to Clipboard"
              className="bg-indigo-500 hover:bg-indigo-400 text-indigo-50 border-indigo-400"
            >
              <ClipboardCopy className="h-3.5 w-3.5 mr-1.5" />
              Copy Response
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        {(() => {
          // Case 1: Initial loading of the component (isExecuting is undefined/null)
          if (typeof isExecuting === "undefined" || isExecuting === null) {
            return (
              <div className="flex flex-col items-center justify-center py-12">
                <Skeleton className="h-6 w-1/3 mb-4" />
                <Skeleton className="h-32 w-full mb-2" />
                <span className="text-muted-foreground text-sm mt-2">
                  Initializing insight area...
                </span>
              </div>
            );
          }

          // Case 2: Actively executing
          if (isExecuting === true) {
            return (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-primary mb-3" />
                <span className="text-muted-foreground text-base font-medium">
                  Generating insight...
                </span>
              </div>
            );
          }

          // From here, isExecuting is false.

          // Case 3: Execution finished with an error
          if (executionError) {
            return (
              <Alert variant="destructive">
                <Terminal className="h-4 w-4" />
                <AlertTitle>Execution Error</AlertTitle>
                <AlertDescription>{executionError}</AlertDescription>
              </Alert>
            );
          }

          // Case 4: Execution finished with a result
          if (executionResult) {
            const displayContent = executionResult.content || "";
            return (
              <>
                {displayContent ? (
                  <div className="markdown-body bg-muted p-4 rounded-md overflow-x-auto">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[
                        rehypeRaw,
                        rehypeHighlight,
                        rehypeSanitize,
                      ]}
                      className="prose prose-sm dark:prose-invert max-w-none"
                      components={components}
                    >
                      {displayContent}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <Alert variant="default" className="mt-4">
                    <Bot className="h-4 w-4 mr-2" />
                    <AlertTitle>No Content Received</AlertTitle>
                    <AlertDescription>
                      The model processed the request but returned no visible
                      content. This might be due to safety filters, an issue
                      with the prompt for this specific model, or if the model's
                      response was genuinely empty. Please check the token usage
                      below (e.g., if output tokens are zero).
                    </AlertDescription>
                  </Alert>
                )}
                <div className="flex justify-between items-center mt-2">
                  {renderUnifiedTokenUsage()}
                  <button
                    onClick={handleCopyToClipboard}
                    className="text-xs text-muted-foreground hover:text-foreground underline cursor-pointer"
                    title="Copy raw model response to clipboard"
                  >
                    Copy raw response (markdown may not render perfectly -
                    especially if you use 'pipes' in campaign names etc)
                  </button>
                </div>
              </>
            );
          }

          // Case 5: Idle state (isExecuting is false, no error, no result)
          return (
            <div className="text-muted-foreground p-6 py-12 text-center">
              <Bot className="w-10 h-10 mx-auto mb-3 text-indigo-400" />
              <p className="font-semibold text-lg mb-1">
                Ready for your insight!
              </p>
              <p className="text-sm">
                Use the controls above to execute the prompt and generate a
                response.
              </p>
            </div>
          );
        })()}
      </CardContent>
    </Card>
  );
};

export default PromptResponseDisplay;
